import { useState, useEffect } from 'react'
import {
    Box,
    Container,
    Typography,
    Grid,
    Paper,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    MenuItem,
    Card,
    CardContent,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import AddIcon from '@mui/icons-material/Add'
import SaveIcon from '@mui/icons-material/Save'
import PrintIcon from '@mui/icons-material/Print'
import { addDoc, collection, serverTimestamp, getDocs } from 'firebase/firestore'
import { db } from '../../../config/firebase'
import { Product, Sale, SaleItem } from '@starwave/shared/src/types'
import toast from 'react-hot-toast'
import InvoiceComponent from './InvoiceComponent'

export default function NewSalePage() {
    const [products, setProducts] = useState<Product[]>([])
    const [loading, setLoading] = useState(true)

    // Sale Form State
    const [customerName, setCustomerName] = useState('')
    const [customerContact, setCustomerContact] = useState('')
    const [paymentMode, setPaymentMode] = useState<'cash' | 'bank_transfer' | 'online' | 'credit'>('cash')
    const [cartItems, setCartItems] = useState<SaleItem[]>([])

    // Temporary Item State
    const [selectedProductId, setSelectedProductId] = useState('')
    const [quantity, setQuantity] = useState(1)

    // Success/Print State
    const [lastSale, setLastSale] = useState<Sale | null>(null)
    const [showSuccessDialog, setShowSuccessDialog] = useState(false)

    useEffect(() => {
        fetchProducts()
    }, [])

    const fetchProducts = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'products'))
            const productsData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Product))
            setProducts(productsData)
        } catch (error) {
            console.error('Error fetching products:', error)
            toast.error('Failed to load products')
        } finally {
            setLoading(false)
        }
    }

    const handleAddItem = () => {
        if (!selectedProductId) return
        const product = products.find(p => p.id === selectedProductId)
        if (!product) return

        const newItem: SaleItem = {
            productId: product.id,
            productName: product.name,
            category: typeof product.category === 'object' ? product.category.main : product.category,
            quantity: Number(quantity),
            unitPrice: Number(product.price),
            unitCost: Number(product.costPrice || 0),
            totalPrice: Number(product.price) * Number(quantity),
            totalProfit: (Number(product.price) - Number(product.costPrice || 0)) * Number(quantity)
        }

        setCartItems([...cartItems, newItem])
        setSelectedProductId('')
        setQuantity(1)
    }

    const handleRemoveItem = (index: number) => {
        const newItems = [...cartItems]
        newItems.splice(index, 1)
        setCartItems(newItems)
    }

    const calculateTotal = () => {
        return cartItems.reduce((acc, item) => acc + item.totalPrice, 0)
    }

    const generateInvoiceNumber = () => {
        return `SW-${Date.now().toString().slice(-6)}`
    }

    const handleSubmit = async () => {
        if (!customerName || cartItems.length === 0) {
            toast.error('Please fill in customer details and add items')
            return
        }

        setLoading(true)
        try {
            const totalAmount = calculateTotal()
            const totalGrossProfit = cartItems.reduce((acc, item) => acc + item.totalProfit, 0)

            const saleData: any = {
                invoiceNumber: generateInvoiceNumber(),
                customerName,
                customerContact,
                items: cartItems,
                totalAmount,
                totalGrossProfit,
                amountReceived: paymentMode === 'credit' ? 0 : totalAmount,
                outstandingAmount: paymentMode === 'credit' ? totalAmount : 0,
                paymentMode,
                date: serverTimestamp(),
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
                createdBy: 'admin'
            }

            const docRef = await addDoc(collection(db, 'sales'), saleData)

            const completedSale: Sale = {
                id: docRef.id,
                ...saleData,
                date: { toDate: () => new Date() } // Mock Timestamp for local display
            } as Sale

            setLastSale(completedSale)
            setShowSuccessDialog(true)
            toast.success('Sale recorded successfully!')

            setCustomerName('')
            setCustomerContact('')
            setCartItems([])
            setPaymentMode('cash')
        } catch (error) {
            console.error('Error recording sale:', error)
            toast.error('Failed to record sale')
        } finally {
            setLoading(false)
        }
    }

    const handlePrint = () => {
        window.print()
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            {/* Hide regular content when printing */}
            <Box sx={{ '@media print': { display: 'none' } }}>
                <Typography variant="h4" gutterBottom fontWeight="bold">
                    New Sale Entry
                </Typography>

                <Grid container spacing={3}>
                    <Grid item xs={12} md={4}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Customer Details</Typography>
                                <Box component="form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        label="Customer Name"
                                        fullWidth
                                        value={customerName}
                                        onChange={(e) => setCustomerName(e.target.value)}
                                        required
                                    />
                                    <TextField
                                        label="Contact Number"
                                        fullWidth
                                        value={customerContact}
                                        onChange={(e) => setCustomerContact(e.target.value)}
                                    />
                                    <TextField
                                        select
                                        label="Payment Mode"
                                        fullWidth
                                        value={paymentMode}
                                        onChange={(e: any) => setPaymentMode(e.target.value)}
                                    >
                                        <MenuItem value="cash">Cash</MenuItem>
                                        <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
                                        <MenuItem value="online">Online</MenuItem>
                                        <MenuItem value="credit">Credit (Outstanding)</MenuItem>
                                    </TextField>
                                </Box>
                            </CardContent>
                        </Card>

                        <Card sx={{ mt: 3 }}>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Add Product</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <TextField
                                        select
                                        label="Select Product"
                                        fullWidth
                                        value={selectedProductId}
                                        onChange={(e) => setSelectedProductId(e.target.value)}
                                    >
                                        {products.map((p) => (
                                            <MenuItem key={p.id} value={p.id}>
                                                {p.name} - PKR {p.price}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        label="Quantity"
                                        type="number"
                                        fullWidth
                                        value={quantity}
                                        onChange={(e) => setQuantity(Number(e.target.value))}
                                        InputProps={{ inputProps: { min: 1 } }}
                                    />
                                    <Button
                                        variant="outlined"
                                        startIcon={<AddIcon />}
                                        onClick={handleAddItem}
                                        disabled={!selectedProductId}
                                    >
                                        Add to Cart
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={8}>
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom>Items</Typography>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Product</TableCell>
                                            <TableCell align="right">Price</TableCell>
                                            <TableCell align="right">Qty</TableCell>
                                            <TableCell align="right">Total</TableCell>
                                            <TableCell align="right">Action</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cartItems.map((item, index) => (
                                            <TableRow key={index}>
                                                <TableCell>{item.productName}</TableCell>
                                                <TableCell align="right">{item.unitPrice}</TableCell>
                                                <TableCell align="right">{item.quantity}</TableCell>
                                                <TableCell align="right">{item.totalPrice}</TableCell>
                                                <TableCell align="right">
                                                    <IconButton color="error" size="small" onClick={() => handleRemoveItem(index)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                        {cartItems.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={5} align="center">No items added</TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow>
                                            <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>Grand Total:</TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
                                                PKR {calculateTotal().toLocaleString()}
                                            </TableCell>
                                            <TableCell />
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                    variant="contained"
                                    size="large"
                                    startIcon={<SaveIcon />}
                                    onClick={handleSubmit}
                                    disabled={loading || cartItems.length === 0}
                                >
                                    Complete Sale
                                </Button>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                <Dialog open={showSuccessDialog} onClose={() => setShowSuccessDialog(false)}>
                    <DialogTitle>Sale Recorded</DialogTitle>
                    <DialogContent>
                        <Typography>
                            Sale has been successfully recorded. With Invoice #{lastSale?.invoiceNumber}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setShowSuccessDialog(false)}>Close</Button>
                        <Button onClick={handlePrint} variant="contained" startIcon={<PrintIcon />}>
                            Print Receipt
                        </Button>
                    </DialogActions>
                </Dialog>
            </Box>

            {/* Print-only Invoice Component */}
            <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
                {lastSale && <InvoiceComponent sale={lastSale} />}
            </Box>
        </Container>
    )
}
